/**
 * Shared catalog of geos the branded HLTV iframe demo surfaces.
 *
 * Consumed by:
 *   - `IframeDemo` (the hosted /iframe-demo page) — drives the geo
 *     combobox for the HLTV preview.
 *   - `HLTVBookmarklet/bookmarkletSource.ts` — JSON-stringified into the
 *     bookmarklet body so the in-page geo picker matches.
 *
 * Keeping a single source of truth here avoids the two surfaces drifting
 * apart silently. Order is intentional — it mirrors the canonical geo
 * list maintained alongside Ghostface so pickers across surfaces show
 * the same sequence (frontend, bookmarklet, admin all line up).
 *
 * The iframe's sponsor resolver lives server-side (partner-api /
 * Ghostface) and accepts any ISO-3166 alpha-2 code; this list is just
 * the curated subset we surface in pickers. Codes outside the list will
 * still work if typed into the bookmarklet's combobox.
 */
export interface SupportedGeo {
  code: string;
  label: string;
}

export const SUPPORTED_GEOS: SupportedGeo[] = [
  { code: "RU", label: "Russia" },
  { code: "PL", label: "Poland" },
  { code: "US", label: "United States" },
  { code: "BR", label: "Brazil" },
  { code: "DE", label: "Germany" },
  { code: "UA", label: "Ukraine" },
  { code: "TR", label: "Turkey" },
  { code: "FI", label: "Finland" },
  { code: "PT", label: "Portugal" },
  { code: "SE", label: "Sweden" },
  { code: "FR", label: "France" },
  { code: "DK", label: "Denmark" },
  { code: "RO", label: "Romania" },
  { code: "GB", label: "United Kingdom" },
  { code: "CZ", label: "Czechia" },
  { code: "KZ", label: "Kazakhstan" },
  { code: "CN", label: "China" },
  { code: "BY", label: "Belarus" },
  { code: "NL", label: "Netherlands" },
  { code: "HK", label: "Hong Kong" },
  { code: "AR", label: "Argentina" },
  { code: "CA", label: "Canada" },
  { code: "ES", label: "Spain" },
  { code: "LT", label: "Republic of Lithuania" },
  { code: "PH", label: "Philippines" },
  { code: "LV", label: "Latvia" },
  { code: "NO", label: "Norway" },
  { code: "AU", label: "Australia" },
  { code: "MN", label: "Mongolia" },
  { code: "EE", label: "Estonia" },
  { code: "HU", label: "Hungary" },
  { code: "SK", label: "Slovakia" },
  { code: "AT", label: "Austria" },
  { code: "VN", label: "Vietnam" },
  { code: "BE", label: "Belgium" },
  { code: "RS", label: "Serbia" },
  { code: "XK", label: "Kosovo" },
  { code: "BG", label: "Bulgaria" },
  { code: "SG", label: "Singapore" },
  { code: "CH", label: "Switzerland" },
  { code: "IN", label: "India" },
  { code: "ID", label: "Indonesia" },
  { code: "JP", label: "Japan" },
  { code: "MD", label: "Republic of Moldova" },
  { code: "MY", label: "Malaysia" },
  { code: "IL", label: "Israel" },
  { code: "IT", label: "Italy" },
  { code: "UZ", label: "Uzbekistan" },
  { code: "MK", label: "Macedonia, the Former Yugoslav Republic of" },
  { code: "TW", label: "Taiwan" },
  { code: "TH", label: "Thailand" },
  { code: "PE", label: "Peru" },
  { code: "IE", label: "Ireland" },
  { code: "KR", label: "South Korea" },
  { code: "GE", label: "Georgia" },
  { code: "CL", label: "Chile" },
  { code: "HR", label: "Croatia" },
  { code: "BA", label: "Bosnia and Herzegovina" },
  { code: "AL", label: "Albania" },
  { code: "SI", label: "Slovenia" },
  { code: "MX", label: "Mexico" },
  { code: "GR", label: "Greece" },
  { code: "KG", label: "Kyrgyzstan" },
  { code: "NZ", label: "New Zealand" },
  { code: "UY", label: "Uruguay" },
  { code: "AE", label: "United Arab Emirates" },
  { code: "CY", label: "Cyprus" },
  { code: "PK", label: "Pakistan" },
  { code: "ZA", label: "South Africa" },
  { code: "AM", label: "Armenia" },
  { code: "CO", label: "Colombia" },
  { code: "IS", label: "Iceland" },
  { code: "MT", label: "Malta" },
  { code: "LU", label: "Luxembourg" },
  { code: "DZ", label: "Algeria" },
  { code: "MM", label: "Myanmar" },
  { code: "ME", label: "Montenegro" },
  { code: "SA", label: "Saudi Arabia" },
  { code: "KH", label: "Cambodia" },
  { code: "BD", label: "Bangladesh" },
  { code: "VE", label: "Venezuela" },
  { code: "EG", label: "Egypt" },
  { code: "MA", label: "Morocco" },
  { code: "IQ", label: "Iraq" },
  { code: "PY", label: "Paraguay" },
  { code: "AZ", label: "Azerbaijan" },
  { code: "EC", label: "Ecuador" },
  { code: "TJ", label: "Tajikistan" },
  { code: "JO", label: "Hashemite Kingdom of Jordan" },
  { code: "MO", label: "Macao" },
  { code: "TN", label: "Tunisia" },
  { code: "BO", label: "Bolivia" },
  { code: "LK", label: "Sri Lanka" },
  { code: "NP", label: "Nepal" },
  { code: "IR", label: "Iran" },
  { code: "LB", label: "Lebanon" },
  { code: "SC", label: "Seychelles" },
  { code: "PS", label: "Palestine" },
  { code: "LI", label: "Liechtenstein" },
  { code: "GT", label: "Guatemala" },
];
